
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ClaimDialogProps {
  centerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  formData: {
    role: string;
    email: string;
    contactPhone: string;
    verificationNote: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (value: string) => void;
  onSubmit: () => void;
}

export function ClaimDialog({
  centerName,
  open,
  onOpenChange,
  isSubmitting,
  formData,
  onInputChange,
  onSelectChange,
  onSubmit,
}: ClaimDialogProps) {
  const isEmailVerified = React.useMemo(() => {
    if (!formData.email || !centerName) return false;
    const emailDomain = formData.email.split('@')[1]?.toLowerCase();
    const centreNameWords = centerName.toLowerCase().split(' ');
    return centreNameWords.some(word => 
      emailDomain?.includes(word.replace(/[^a-z0-9]/g, ''))
    );
  }, [formData.email, centerName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Claim {centerName}</DialogTitle>
          <DialogDescription>
            Complete this form to request ownership of this nursing home profile. Our team will review your submission and verify your association with the facility.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="role">Your Role at {centerName}</Label>
            <Select
              onValueChange={onSelectChange}
              defaultValue={formData.role}
            >
              <SelectTrigger id="role">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="administrator">Administrator</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="marketing">Marketing Manager</SelectItem>
                <SelectItem value="staff">Staff Member</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Work Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={onInputChange}
              placeholder="Enter your work email"
            />
            {formData.email && !isEmailVerified && (
              <Alert variant="default" className="bg-amber-50 border-amber-200">
                <AlertDescription className="text-amber-800">
                  Please use your work email associated with {centerName}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="contactPhone">Contact Phone</Label>
            <Input
              id="contactPhone"
              name="contactPhone"
              value={formData.contactPhone}
              onChange={onInputChange}
              placeholder="Enter your phone number"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="verificationNote">Additional Information</Label>
            <Textarea
              id="verificationNote"
              name="verificationNote"
              value={formData.verificationNote}
              onChange={onInputChange}
              placeholder="Please provide any additional information that could help verify your relationship with this nursing home"
              rows={3}
            />
          </div>

          <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
            <Info className="h-4 w-4" />
            <span>
              {isEmailVerified 
                ? "Your claim will be processed automatically based on your work email."
                : "A team member will contact you to verify your association with the facility."}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onSubmit}
            disabled={!formData.role || !formData.email || !formData.contactPhone || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Claim'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
